// The pure, testable CORE of user-prefs persistence. Two properties matter and both are timing-sensitive, so they
// live here (no Apollo, no reactivity) where a simulation can drive them:
//   1. SERIALIZE — every write chains through one promise, so a save is never dropped because another is in flight.
//   2. FRESH READ — inside the chain each write reads the LATEST blob before writing, so concurrent namespace writes
//      can't clobber each other (the shared blob is read-modify-write).
// `usePreferencesNamespace` injects getBlob/getOrg/mutate (Apollo-backed); a test injects fakes with a random latency.

const ORG_PREFIX = "organizationId_";

export type BlobWriterDeps = {
  /** the latest persisted blob (Apollo cache in prod; the fake backend in a test). */
  getBlob: () => Record<string, unknown> | undefined;
  getOrg: () => string | undefined;
  /** persist the whole blob (the GraphQL mutation in prod; a delayed fake in a test). */
  mutate: (blob: Record<string, unknown>) => Promise<unknown>;
};

/** ONE namespaced write against the freshest blob. No-op if there's no org yet. */
export async function writeNamespaceToBlob(deps: BlobWriterDeps, namespaceName: string, prefs: unknown): Promise<void> {
  const org = deps.getOrg();
  if (!org) return;
  const cur = deps.getBlob() ?? {};
  // First time we scope to an org, `cur[org]` is undefined — fall back to the whole blob (migrating existing prefs),
  // then strip the org-scoped keys below so they don't nest.
  const prefsForOrg = (cur[org] as Record<string, unknown> | undefined) ?? cur;
  const scoped: Record<string, unknown> = { ...prefsForOrg, [namespaceName]: prefs };
  for (const key of Object.keys(scoped)) {
    if (key.startsWith(ORG_PREFIX)) delete scoped[key];
  }
  await deps.mutate({ ...cur, [org]: scoped });
}

/** A serialized writer: every write it hands out chains behind the previous one, so none is dropped under load and
 *  none races the shared blob. Create ONE and share it across all namespaces (the blob is shared). */
export function createSerializedBlobWriter() {
  let chain: Promise<unknown> = Promise.resolve();
  return (deps: BlobWriterDeps, namespaceName: string, prefs: unknown): Promise<void> => {
    const run = () => writeNamespaceToBlob(deps, namespaceName, prefs);
    const settled = chain.then(run, run); // continue the chain even if a prior write rejected
    chain = settled;
    return settled;
  };
}

/** The app-wide writer — every `usePreferencesNamespace` instance serializes through this one (shared blob). */
export const sharedBlobWriter = createSerializedBlobWriter();
